import { createFileRoute } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityFeedRow } from '@/activities/ActivityFeedRow';
import api from '@/api';
import { AlertBox } from '@/common/AlertBox';
import { LoadingSpinner } from '@/common/LoadingSpinner';
import { LoadMoreWrapper, TimelineWrapper } from '@/common/timelineStyles';
import { WBSButton } from '@/common/WBSButton';
import { GroupHeaderWrapper, Title } from '~/styles/layoutElements';

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/activity',
)({
  component: RouteComponent,
});

/**
 * Legacy's page size for this tab's Load More, sent explicitly because the
 * shared pagination default is 10.
 */
const PAGE_SIZE = 20;

function RouteComponent(): ReactElement {
  const { t } = useTranslation();
  const { groupName } = Route.useParams();

  // Resolve the slug to an id.
  const { data: group, isError: groupFailed } = api.getGroupBySlug.useQuery({
    path: { group_slug: groupName },
  });
  // qraft evaluates `parameters` before reading `enabled`, and `group` is
  // undefined until the slug resolves, so 0 (never a real id) stands in.
  // `group!.id` would throw on the refusal path below.
  const groupId = group?.id ?? 0;

  const {
    data,
    isError: feedFailed,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = api.listGroupActivity.useInfiniteQuery(
    { path: { group_id: groupId }, query: { limit: PAGE_SIZE } },
    {
      enabled: groupId > 0,
      initialPageParam: { query: { offset: 0 } },
      // `PaginatedResponse` has no `hasMore`, so it's derived from `count`. The
      // offset is the number of rows already held rather than page index times
      // size; the endpoint's tiebreak keeps the window stable when timestamps
      // collide.
      getNextPageParam: (lastPage, allPages) => {
        const loaded = allPages.reduce((n, page) => n + page.data.length, 0);
        return loaded < lastPage.count
          ? { query: { offset: loaded } }
          : undefined;
      },
    },
  );

  const items = data?.pages.flatMap((page) => page.data) ?? [];
  // A 403 or 404 on either query resolves the screen. Query errors aren't
  // caught above this route, so otherwise a refused caller sits on a spinner:
  // the feed query stays disabled when the slug read fails, and an untyped
  // group's 404 arrives on the feed query itself.
  const refused = groupFailed || feedFailed;
  // A pending read shows the heading and a spinner, never the empty state: "no
  // activity found" before anything is read would be wrong, and for a refusal
  // it would reveal whether the group is empty.
  const pending = !refused && (group === undefined || data === undefined);

  return (
    <div>
      <GroupHeaderWrapper>
        <Title>{t('sidebar.activity-log')}</Title>
      </GroupHeaderWrapper>
      {refused ? (
        // Legacy's message for a non-member of a hidden group.
        <AlertBox variant="error">{t('wbs.page-not-found')}</AlertBox>
      ) : pending ? (
        <LoadingSpinner centered />
      ) : items.length === 0 ? (
        // Legacy shows this for an empty group and an exhausted feed alike.
        <AlertBox variant="info">{t('wbs.sorry-there-was-no')}</AlertBox>
      ) : (
        <TimelineWrapper>
          {items.map((item) => (
            <ActivityFeedRow key={item.id} item={item} />
          ))}
          {hasNextPage && (
            <LoadMoreWrapper>
              <WBSButton
                wide
                alternate
                isLoading={isFetchingNextPage}
                onClick={() => {
                  void fetchNextPage();
                }}
              >
                {t('wbs.load-more')}
              </WBSButton>
            </LoadMoreWrapper>
          )}
        </TimelineWrapper>
      )}
    </div>
  );
}
